import { Dialog, DialogContent, DialogTitle, DialogActions, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import { UnifiedContactSelector, SelectedContact, getContactSerializedId } from '@extension/context/content/components/shared';
import { WaContact } from '@common/models/waContact';
import { getWaContactDisplayLabel } from '@common-reducers/WhatsAppSelectors';
import { usePortalContainer } from '@/common/hooks/use-portal-container';
import type { TypedRecipient } from '@common/utils/phone-validation-utils';

export interface AddContactsToCampaignDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (contacts: SelectedContact[]) => void;
    // Pre-select contacts when the dialog opens (e.g. when editing an existing
    // row's contact, the row's current contact is pre-checked).
    initialSelected?: SelectedContact[];
    // When false, the list renders radio buttons and clicking a new contact
    // REPLACES the current selection instead of toggling. Used by edit-cell
    // flows where the dialog manages a single value, not a set.
    isMultipleSelection?: boolean;
}

const AddContactsToCampaignDialog: FC<AddContactsToCampaignDialogProps> = ({ open, onClose, onConfirm, initialSelected, isMultipleSelection = true }) => {
    const { t } = useTranslation(namespace);
    const [selected, setSelected] = useState<SelectedContact[]>(initialSelected ?? []);
    // Resolves to .btcss-portal-container inside the extension's Shadow DOM, or
    // document.body on the website. Must be an HTMLElement — MUI's ModalManager
    // crashes on a ShadowRoot when it tries to set container.style.overflow.
    // The portal-container has `pointer-events: none`, which we counter below by
    // forcing `pointer-events: auto` on the Modal root + Paper inline.
    const portalContainer = usePortalContainer();
    const dialogContainer = portalContainer instanceof ShadowRoot
        ? (portalContainer.host as HTMLElement)
        : (portalContainer as HTMLElement | undefined);

    // Reset to the caller's pre-selection each time the dialog opens, and clear
    // on close so a future open with a different initialSelected starts clean.
    useEffect(() => {
        if (open) setSelected(initialSelected ?? []);
        else setSelected([]);
    }, [open, initialSelected]);

    const selectedIds = useMemo(() => selected.map(c => c.id), [selected]);

    const handleContactClick = useCallback((contact: WaContact) => {
        const id = getContactSerializedId(contact);
        if (!id) return;
        const label = getWaContactDisplayLabel(contact);
        const entry: SelectedContact = {
            id,
            name: label,
            displayName: label,
            imgUrl: (contact as any)?.imgUrl,
        };
        setSelected(prev => {
            if (!isMultipleSelection) {
                // Single-select: clicking the same contact deselects; clicking a
                // different one replaces. Result always has at most one entry.
                return prev[0]?.id === id ? [] : [entry];
            }
            return prev.some(c => c.id === id)
                ? prev.filter(c => c.id !== id)
                : [...prev, entry];
        });
    }, [isMultipleSelection]);

    const handleContactRemove = useCallback((contactId: string) => {
        setSelected(prev => prev.filter(c => c.id !== contactId));
    }, []);

    const handleClearAll = useCallback(() => setSelected([]), []);

    const handleFreeInputAdd = useCallback(({ chatId: id, label }: TypedRecipient) => {
        const entry: SelectedContact = { id, name: label, displayName: label };
        setSelected(prev => {
            if (!isMultipleSelection) return [entry];
            if (prev.some(c => c.id === id)) return prev;
            return [...prev, entry];
        });
    }, [isMultipleSelection]);

    const handleConfirm = () => {
        onConfirm(selected);
        onClose();
    };

    const confirmLabel = () => {
        if (initialSelected) return t('campaign.addContacts.save', 'Save');
        if (selected.length === 0) return t('campaign.addContacts.confirmEmpty', 'Add contacts');
        // ONE counted key, not a one/many pair: Hebrew inflects TWO differently
        // from three, which no English-shaped `=== 1` branch can express.
        return t('campaign.addContacts.confirmCount', 'Add {{count}} contacts', { count: selected.length });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            container={dialogContainer}
            // The container chain (e.g. .btcss-portal-container in the extension)
            // has pointer-events:none, which cascades down. MUI's stylesheet that
            // re-enables pointer-events:auto on .MuiDialog-paper doesn't reach
            // the Paper through the shadow DOM. Inline `style` on the Modal root
            // AND the Paper guarantees clicks land regardless of style isolation.
            sx={{ pointerEvents: 'auto' }}
            PaperProps={{
                sx: { height: '80vh', maxHeight: 720 },
                style: { pointerEvents: 'auto' },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                {t('campaign.addContacts.title', 'Add contacts to campaign')}
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div data-testid="add-contacts-selector" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    <UnifiedContactSelector
                        multiSelect={isMultipleSelection}
                        singleSelect={!isMultipleSelection}
                        showTypeFilter
                        showRefreshButton
                        allowFreeInput
                        onFreeInputAdd={handleFreeInputAdd}
                        selectedContactIds={selectedIds}
                        selectedContacts={selected}
                        onContactClick={handleContactClick}
                        onContactRemove={handleContactRemove}
                        onClearAll={handleClearAll}
                        height="100%"
                    />
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    // Add mode (no initial selection): disabled until user picks at least one.
                    // Edit mode (initialSelected provided): always enabled — confirming with
                    // an empty selection means "clear/remove" and is a valid choice.
                    disabled={!initialSelected && selected.length === 0}
                    data-testid="add-contacts-confirm"
                >
                    {confirmLabel()}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddContactsToCampaignDialog;
